import { emptyTotal, emptyResources } from './useQueryResourceList.js';
import { isWoodyError } from '../../errors/errors.utils.js';
import { useInfiniteQuery, keepPreviousData, hashKey } from '@tanstack/react-query';
import { resourceQueryKeys } from './resourceQueryKeys.js';
import { useWoody } from '../../woody/woody.js';
import { isQueryEnabled } from '@fabric-app/queries/utilities';
import { useContextAccessToken } from '../../permission/contexts/AnonShareTokenContext.js';
import { omitProperties } from '@fabric-app/utils/object/manipulation';

const filterSelector = (data) => {
  const allResources = data.pages.flatMap((page) => page.resources);
  const hasReachedEnd = data.pages.length > 0 && !data.pages[data.pages.length - 1].hasMore;
  return {
    /**
    * The elastic search total could be innacurate, so if we reached the end of the results we rely on the total resources gotten
    * from the query. Otherwise we use the total from the first page.
    */
    total: hasReachedEnd ? allResources.length : data.pages[0].total,
    queryHash: data.pages[0]._meta.queryHash,
    pages: data.pages.map((page) => ({
      pageTotal: page.resources.length
    })),
    resources: allResources
  };
};
const useQueryResourceContextSearch = (params, queryOptions) => {
  const { client } = useWoody();
  const accessToken = useContextAccessToken();
  const pageSize = params?.pageSize ?? 20;
  const query = useInfiniteQuery({
    queryKey: resourceQueryKeys.resourceContextSearchList(params),
    queryFn: async ({ pageParam, signal, queryKey }) => {
      const pagination = {
        page: pageParam ?? 1,
        pageSize
      };
      const pageData = await client.v2("/v2/search", {
        method: "post",
        body: {
          ...params,
          filters: omitProperties(params.filters || {}, [
            omitProperties.isUndefined,
            omitProperties.isEmptyArray
          ]),
          pagination,
          /**
          * do not pass name, this breaks the BE
          */
          sort: params.sort && params.sort.field === "name" ? void 0 : params.sort
        },
        query: {
          accessToken
        },
        signal
      });
      return {
        // This allows the select function to have access to the options used in the query
        // And we avoid having to memoize the select function
        nextCursor: null,
        total: pageData.total,
        hasMore: pageData.hasMore,
        resources: pageData.hits,
        _meta: {
          queryHash: hashKey(queryKey),
          options: params,
          pagination
        }
      };
    },
    placeholderData: keepPreviousData,
    staleTime: 15e3,
    refetchInterval: 45e3,
    retry: (failureCount, error) => {
      if (isWoodyError(error) && error.status === 404) {
        return failureCount < 1;
      }
      return failureCount < 4;
    },
    ...queryOptions,
    select: filterSelector,
    enabled: isQueryEnabled([
      queryOptions?.enabled,
      !!params
    ]),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage._meta.pagination.page + 1 : void 0;
    }
  });
  return {
    ...query,
    resources: query.data?.resources || emptyResources,
    total: query.data?.total ?? emptyTotal,
    queryHash: query.data?.queryHash
  };
};

export { useQueryResourceContextSearch };
//# sourceMappingURL=useQueryResourceContextSearch.js.map
